/**
 * Git metadata collection and project ID normalization.
 *
 * Collects branch, commit, message, and remote URL using git CLI.
 * Each field fails independently — a missing remote doesn't prevent
 * branch detection.
 *
 * Uses `execSync` for simplicity (< 100ms total, runs at startup only).
 */

import { execSync } from 'node:child_process';
import { readFileSync } from 'node:fs';
import { basename, join } from 'node:path';
import type { GitMetadata } from '@testrelic/core';

// ---------------------------------------------------------------------------
// Git Command Execution
// ---------------------------------------------------------------------------

const GIT_TIMEOUT_MS = 5000;

/** Run a git command and return stdout, or null on any failure. */
function execGit(command: string, cwd?: string): string | null {
  try {
    const result = execSync(command, {
      cwd,
      timeout: GIT_TIMEOUT_MS,
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    });
    return result.trim() || null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Git Metadata Collection
// ---------------------------------------------------------------------------

/**
 * Collect git metadata from the current working directory.
 * Each field is collected independently — failures don't cascade.
 * Non-git projects return all nulls without error.
 */
export function collectGitMetadata(cwd?: string): GitMetadata {
  const branch = execGit('git rev-parse --abbrev-ref HEAD', cwd);
  const commitSha = execGit('git rev-parse --short HEAD', cwd);
  const commitMessage = execGit('git log -1 --pretty=%s', cwd);
  const commitAuthor =
    execGit('git log -1 --format=%an', cwd) ??
    execGit('git config user.name', cwd);
  const rawRemoteUrl = getRemoteUrl(cwd);
  const remoteUrl = rawRemoteUrl ? normalizeGitRemoteUrl(rawRemoteUrl) : null;

  return { branch, commitSha, commitMessage, commitAuthor, remoteUrl };
}

/**
 * Get the remote URL, preferring `origin` but falling back to the first
 * available remote. Handles monorepos where the remote may not be named `origin`.
 */
function getRemoteUrl(cwd?: string): string | null {
  // Try origin first (most common)
  const originUrl = execGit('git remote get-url origin', cwd);
  if (originUrl) return originUrl;

  // List all remotes and use the first one
  const remotes = execGit('git remote', cwd);
  if (!remotes) return null;

  const firstRemote = remotes.split('\n')[0]?.trim();
  if (!firstRemote) return null;

  return execGit(`git remote get-url ${firstRemote}`, cwd);
}

// ---------------------------------------------------------------------------
// Remote URL Normalization
// ---------------------------------------------------------------------------

/**
 * Normalize a git remote URL into a stable project identifier.
 *
 * Strips protocol, replaces `:` with `/`, strips `.git` suffix, lowercases.
 *
 * Examples:
 *   https://github.com/acme/checkout-service.git → github.com/acme/checkout-service
 *   git@github.com:acme/checkout-service.git → github.com/acme/checkout-service
 */
export function normalizeGitRemoteUrl(url: string): string {
  let normalized = url.trim();

  // Strip protocol (https://, http://, git://, ssh://)
  normalized = normalized.replace(/^[a-z+]+:\/\//, '');

  // Handle SSH format: git@host:path → host/path
  normalized = normalized.replace(/^[^@]+@/, '');
  normalized = normalized.replace(/:(?!\d)/, '/');

  // Strip .git suffix
  normalized = normalized.replace(/\.git$/, '');

  // Strip trailing slashes
  normalized = normalized.replace(/\/+$/, '');

  // Strip authentication (user:pass@)
  normalized = normalized.replace(/^[^@/]+@/, '');

  return normalized.toLowerCase();
}

// ---------------------------------------------------------------------------
// Repo Display Name
// ---------------------------------------------------------------------------

/**
 * Derive a human-readable repo display name from a normalized git remote URL
 * or from a directory path.
 *
 * Returns the last path segment (e.g., "checkout-service" from "github.com/acme/checkout-service").
 */
export function deriveRepoDisplayName(normalizedUrl: string): string {
  const segments = normalizedUrl.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? normalizedUrl;
}

/**
 * Read the `name` field from a package.json in the given directory.
 * Returns null if the file doesn't exist or can't be parsed.
 */
export function readPackageJsonName(dirPath: string): string | null {
  try {
    const raw = readFileSync(join(dirPath, 'package.json'), 'utf-8');
    const pkg = JSON.parse(raw) as Record<string, unknown>;
    return typeof pkg.name === 'string' && pkg.name.length > 0 ? pkg.name : null;
  } catch {
    return null;
  }
}

/**
 * Derive a project git ID for non-git projects.
 * Tries package.json `name` first, falls back to `local/{dirname}`.
 */
export function deriveNonGitProjectId(dirPath: string): string {
  const pkgName = readPackageJsonName(dirPath);
  if (pkgName) return pkgName;
  return `local/${basename(dirPath)}`;
}
